import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Users,
  CheckSquare,
  Calendar,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Clock,
  Phone,
  Mail
} from 'lucide-react';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RepDetailPage() {
  const { repId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchRepDetail = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/intelligence/rep/${repId}`,
          { credentials: 'include' }
        );
        
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (error) {
        logger.error('Error fetching rep detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRepDetail();
  }, [repId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-slate-500">Rep not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="rep-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{data.rep_info?.name}</h1>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <Badge variant="outline">{data.rep_info?.role}</Badge>
            {data.rep_info?.email && (
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" /> {data.rep_info?.email}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Users className="h-4 w-4" /> Total Leads
            </div>
            <div className="text-2xl font-bold">{data.lead_metrics?.total_leads || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <TrendingUp className="h-4 w-4" /> Close Rate
            </div>
            <div className="text-2xl font-bold text-green-600">{data.lead_metrics?.close_rate || 0}%</div>
            <div className="text-xs text-slate-400">{data.lead_metrics?.closed_won} won</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <CheckSquare className="h-4 w-4" /> Task Completion
            </div>
            <div className="text-2xl font-bold">{data.task_metrics?.task_completion_rate || 0}%</div>
            <div className="text-xs text-slate-400">{data.task_metrics?.completed_tasks}/{data.task_metrics?.total_tasks}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Calendar className="h-4 w-4" /> Show Rate
            </div>
            <div className="text-2xl font-bold">{data.appointment_metrics?.show_rate || 0}%</div>
            <div className="text-xs text-slate-400">{data.appointment_metrics?.completed_appointments} attended</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <DollarSign className="h-4 w-4" /> Collected (Month)
            </div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(data.financial_metrics?.collected_this_month)}</div>
            <div className="text-xs text-slate-400">{data.financial_metrics?.collection_rate}% of expected</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <AlertTriangle className="h-4 w-4" /> Overdue
            </div>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(data.financial_metrics?.overdue_amount)}</div>
            <div className="text-xs text-slate-400">{data.financial_metrics?.overdue_installments} installments</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Breakdown by Stage */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Lead Breakdown by Stage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.lead_metrics?.leads_by_stage || {}).map(([stage, count]) => {
                const maxCount = Math.max(...Object.values(data.lead_metrics?.leads_by_stage || {}), 1);
                const widthPercent = (count / maxCount) * 100;
                const isTerminal = stage === 'closed_won' || stage === 'closed_lost';
                
                return (
                  <div key={stage}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-600">{stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded overflow-hidden">
                      <div 
                        className={`h-full rounded ${isTerminal ? (stage === 'closed_won' ? 'bg-green-500' : 'bg-red-400') : 'bg-blue-500'}`}
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Financial Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Financial Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500">Total Contracts</div>
                  <div className="text-xl font-bold">{data.financial_metrics?.total_contracts || 0}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500">Contract Value</div>
                  <div className="text-xl font-bold">{formatCurrency(data.financial_metrics?.total_contract_value)}</div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-xs text-green-600">Collected This Month</div>
                  <div className="text-xl font-bold text-green-700">{formatCurrency(data.financial_metrics?.collected_this_month)}</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-xs text-blue-600">Expected This Month</div>
                  <div className="text-xl font-bold text-blue-700">{formatCurrency(data.financial_metrics?.expected_this_month)}</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <div className="text-xs text-amber-600">Total Receivable</div>
                  <div className="text-xl font-bold text-amber-700">{formatCurrency(data.financial_metrics?.total_receivable)}</div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <div className="text-xs text-red-600">Overdue Amount</div>
                  <div className="text-xl font-bold text-red-700">{formatCurrency(data.financial_metrics?.overdue_amount)}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Tasks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-slate-400" />
            Recent Tasks
            <Badge variant="outline" className="ml-2">
              {data.task_metrics?.overdue_tasks || 0} overdue
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.task_metrics?.recent_tasks?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.task_metrics.recent_tasks.map((task) => (
                  <TableRow key={task.task_id}>
                    <TableCell className="font-medium">{task.title}</TableCell>
                    <TableCell>{task.task_type}</TableCell>
                    <TableCell>{task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <Badge variant={task.status === 'completed' ? 'default' : task.status === 'overdue' ? 'destructive' : 'secondary'}>
                        {task.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={task.priority === 'high' ? 'destructive' : 'outline'}>
                        {task.priority}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-slate-400">No recent tasks</div>
          )}
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-400" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.activity_timeline?.length > 0 ? (
            <div className="space-y-3">
              {data.activity_timeline.slice(0, 10).map((activity) => (
                <div key={activity.timestamp || activity.date} className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    activity.channel === 'Phone' ? 'bg-green-500' : 
                    activity.channel === 'Email' ? 'bg-blue-500' : 'bg-slate-400'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{activity.channel || activity.type}</Badge>
                      <span className="text-xs text-slate-400">
                        {activity.date ? new Date(activity.date).toLocaleString() : '-'}
                      </span>
                    </div>
                    {activity.notes && (
                      <p className="text-sm text-slate-600 mt-1">{activity.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">No recent activity</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
